import { CancelToken } from "axios";
import { FC, ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { ClientRevenueType, IClientRevenueItem } from "../../models/client";
import { IApplicationState } from "../../store";
import { IFilterType } from "../../store/filterType";
import {
  changeClientRevenueListFilter,
  changeClientRevenueListOrder,
  changeClientRevenueListPage,
  getClientRevenueList,
  getClientRevenueListCount,
  IClientRevenueListState,
  selectClientRevenueList,
  clientRevenueListLoad,
} from "../../store/clientRevenueList";
import FilterPanel from "../common/filter/FilterPanel";
import Grid from "../common/grid/Grid";
import {
  GridIconAdd,
  GridIconDelete,
  GridIconEdit,
  GridIconSpace,
} from "../common/grid/GridIcons";
import { TableCol, TableColInput } from "../common/grid/TableCol";
import { RightType } from "../../models/auth";
import { hasClientWriteRight } from "../../utils/rights";
import { selectIdentityRights } from "../../store/identity";
import { selectClientId } from "../../store/client";
import { Form, Formik, useFormikContext } from "formik";
import Input from "../common/form/Input";
import { GridInlineButtons } from "../common/grid/GridInline";
import * as Yup from "yup";
import clientApi from "../../api/client";
import validations from "../../utils/validations";
import {
  promiseToastDeleteNoException,
  promiseToastSave,
} from "../../utils/toasts";
import { ModalYesNoFunction } from "../common/modal/ModalFunctions";

const defaultData = {
  id: 0,
  type: ClientRevenueType.Salary,
  amountPerMonth: 0,
  description: "",
} as IClientRevenueItem;

interface IProps {
  prov: IClientRevenueListState;
  clientId?: number;
  identityRights?: RightType[];
  getClientRevenueList(clientId: number, cancelToken: CancelToken): void;
  getClientRevenueListCount(clientId: number, cancelToken: CancelToken): void;
  changeClientRevenueListOrder(orderBy: string, orderDesc: boolean): void;
  changeClientRevenueListPage(page: number): void;
  changeClientRevenueListFilter(filter: IFilterType): void;
  clientRevenueListLoad(reload: boolean): void;
}

const ClientFinanceRevenueGridEditor: FC<{
  data: IClientRevenueItem;
  handleCancel(): void;
}> = ({ data, handleCancel }) => {
  const { t } = useTranslation();
  const {
    errors,
    touched,
    isSubmitting,
    handleSubmit,
    resetForm,
    setFieldValue,
  } = useFormikContext<IClientRevenueItem>();

  useEffect(() => {
    resetForm();
    setFieldValue("type", data.type);
    setFieldValue(
      "amountPerMonth",
      data.amountPerMonth ? data.amountPerMonth : ""
    );
    setFieldValue("description", data.description ?? "");
  }, [data, resetForm, setFieldValue]);

  return (
    <>
      <TableColInput>
        <Input
          name="type"
          error={touched.type && !!errors.type}
          component="select"
        >
          {(
            Object.keys(ClientRevenueType) as Array<
              keyof typeof ClientRevenueType
            >
          ).map((key) => (
            <option key={key} value={ClientRevenueType[key]}>
              {t("client.finance.revenue.types." + ClientRevenueType[key])}
            </option>
          ))}
        </Input>
      </TableColInput>
      <TableColInput>
        <Input
          name="amountPerMonth"
          type="number"
          error={touched.amountPerMonth && !!errors.amountPerMonth}
        />
      </TableColInput>
      <TableColInput>
        <Input
          name="description"
          error={touched.description && !!errors.description}
          maxLength={512}
        />
      </TableColInput>
      <GridInlineButtons
        handleCancel={handleCancel}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
      />
    </>
  );
};

const ClientFinanceRevenue: FC<IProps> = ({
  prov,
  clientId,
  identityRights,
  getClientRevenueList,
  getClientRevenueListCount,
  changeClientRevenueListOrder,
  changeClientRevenueListPage,
  changeClientRevenueListFilter,
  clientRevenueListLoad,
}) => {
  const { t } = useTranslation();
  const [inlineEditId, setInlineEditId] = useState<number | undefined>();
  const [inlineAdd, setInlineAdd] = useState(false);

  useEffect(() => {
    clientRevenueListLoad(false);
  }, [clientRevenueListLoad]);

  const handleEdit = (id: number) => {
    setInlineAdd(false);
    setInlineEditId(id);
  };

  const handleDelete = async (id: number) => {
    const res = await ModalYesNoFunction(
      t("client.finance.revenue.delete"),
      t("client.finance.revenue.deleteConfirm")
    );
    if (res) {
      await promiseToastDeleteNoException(async () => {
        await clientApi.deleteClientRevenue(id);
        clientRevenueListLoad(true);
      });
    }
  };

  const handleCancel = () => {
    setInlineEditId(undefined);
    setInlineAdd(false);
  };

  const handleAdd = () => {
    setInlineEditId(undefined);
    setInlineAdd(true);
  };

  const handleRenderData = (item: IClientRevenueItem): ReactElement => {
    return (
      <>
        <TableCol>{t("client.finance.revenue.types." + item.type)}</TableCol>
        <TableCol>{item.amountPerMonth}</TableCol>
        <TableCol>{item.description}</TableCol>
        <TableCol>
          {!inlineEditId &&
            !inlineAdd &&
            hasClientWriteRight(identityRights, [
              RightType.WriteClientsFinancingData,
            ]) && (
              <>
                <GridIconEdit onClick={() => handleEdit(item.id)} />
                <GridIconSpace />
                <GridIconDelete onClick={() => handleDelete(item.id)} />
              </>
            )}
        </TableCol>
      </>
    );
  };

  const handleSubmit = async (data: IClientRevenueItem) => {
    await promiseToastSave(async () => {
      if (inlineEditId) {
        await clientApi.updateClientRevenue(inlineEditId, data);
      } else {
        await clientApi.createClientRevenue(clientId!, data);
      }
      clientRevenueListLoad(true);
      handleCancel();
    });
  };

  const handleRenderEditor = (item: IClientRevenueItem): ReactElement => {
    return (
      <ClientFinanceRevenueGridEditor data={item} handleCancel={handleCancel} />
    );
  };

  const handleRowClick = (item: IClientRevenueItem) => {
    setInlineEditId(item.id);
  };

  const handleGetData = (cancelToken: CancelToken) =>
    getClientRevenueList(clientId!, cancelToken);

  const handleGetCount = (cancelToken: CancelToken) =>
    getClientRevenueListCount(clientId!, cancelToken);

  return (
    <>
      <FilterPanel
        viewFiltration={!inlineEditId && !inlineAdd}
        title={t("client.finance.revenue.detailTitle")}
        name="clientRevenue"
        filter={prov.filter!}
        changeFilter={changeClientRevenueListFilter}
      />
      <Formik
        initialValues={defaultData}
        validationSchema={Yup.object({
          amountPerMonth: validations.numberRequiredMinMax(1, 999999, t),
        })}
        validateOnMount={true}
        onSubmit={handleSubmit}
      >
        <Form>
          <Grid<IClientRevenueItem>
            headers={[
              { captionStr: "client.finance.revenue.type" },
              { captionStr: "client.finance.revenue.amountPerMonth" },
              { captionStr: "client.finance.revenue.description" },
              {
                captionEl:
                  !inlineEditId &&
                  !inlineAdd &&
                  hasClientWriteRight(identityRights, [
                    RightType.WriteClientsFinancingData,
                  ]) ? (
                    <GridIconAdd onClick={handleAdd} />
                  ) : undefined,
              },
            ]}
            inlineEditId={inlineEditId}
            inlineAdd={inlineAdd}
            defaultData={defaultData}
            renderData={handleRenderData}
            renderEditor={handleRenderEditor}
            getData={handleGetData}
            getCount={handleGetCount}
            changeOrder={changeClientRevenueListOrder}
            changePage={changeClientRevenueListPage}
            onRowClick={
              hasClientWriteRight(identityRights, [
                RightType.WriteClientsFinancingData,
              ])
                ? handleRowClick
                : undefined
            }
            prov={prov}
          />
        </Form>
      </Formik>
    </>
  );
};

const mapStateToProps = (state: IApplicationState) => {
  return {
    prov: selectClientRevenueList(state),
    clientId: selectClientId(state),
    identityRights: selectIdentityRights(state),
  };
};

const mapDispachToProps = {
  getClientRevenueList,
  getClientRevenueListCount,
  changeClientRevenueListOrder,
  changeClientRevenueListPage,
  changeClientRevenueListFilter,
  clientRevenueListLoad,
};

export default connect(
  mapStateToProps,
  mapDispachToProps
)(ClientFinanceRevenue);
